import { create } from 'zustand'

/**
 * Canonical module-policy snapshot store — gating migration Phase 3.
 *
 * Holds the effective module-access map fetched from GET /api/policy/modules
 * (see backend/src/lib/module-policy-resolver.js). This is the single source
 * the route guards and nav gating read from.
 *
 * `modules` is null until the snapshot has loaded — callers MUST treat null as
 * "snapshot unavailable" and fall back to the legacy store-derived gating
 * (useSettingsStore). The fallback is kept until backend parity is proven; it
 * is removed in migration Phase 6.
 */
/**
 * Effective access verdict for a single module, as resolved by
 * backend/src/lib/module-policy-resolver.js.
 */
export interface ModuleVerdict {
  module: string
  allowed: boolean
  reason?: string | null
  source?: string | null
  explanation?: string | null
  [key: string]: unknown
}

/** A GET /api/policy/modules response snapshot. */
export interface PolicySnapshot {
  plan?: string | null
  modules?: ModuleVerdict[]
}

export interface PolicyState {
  plan: string | null
  /** null = snapshot not loaded → callers fall back to legacy gating. */
  modules: Record<string, ModuleVerdict> | null
  loaded: boolean
  setPolicy: (snapshot: PolicySnapshot | null | undefined) => void
  getModuleVerdict: (key: string) => ModuleVerdict | null
  isModuleAllowed: (key: string) => boolean
  resetPolicy: () => void
}

export const usePolicyStore = create<PolicyState>((set, get) => ({
  plan: null,
  // null = snapshot not loaded → callers fall back to legacy gating.
  // Otherwise: { [moduleKey]: { module, allowed, reason, source, explanation } }
  modules: null,
  loaded: false,

  /** Ingest a GET /api/policy/modules response. */
  setPolicy: (snapshot) => set({
    plan: snapshot?.plan ?? null,
    modules: Array.isArray(snapshot?.modules)
      ? Object.fromEntries(snapshot.modules.map((m) => [m.module, m]))
      : null,
    loaded: true,
  }),

  /** Effective verdict for one module, or null when the snapshot has not loaded. */
  getModuleVerdict: (key) => {
    const { modules } = get()
    return modules ? (modules[key] || null) : null
  },

  /** Convenience boolean: true when allowed, true when snapshot absent (fallback). */
  isModuleAllowed: (key) => {
    const v = get().getModuleVerdict(key)
    return v ? v.allowed : true
  },

  resetPolicy: () => set({ plan: null, modules: null, loaded: false }),
}))

export default usePolicyStore
